
import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;

      if (
        mutation.attributeName === 'class' &&
        this.classList.contains('hidden')
      ) continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) continue;

      this.#loadRecommendations();
      break;
    }
  });

  /** @type {Record<string, string>} */
  #cachedRecommendations = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #loadRecommendations() {
    const {
      productId,
      recommendationsPerformed,
      sectionId,
      intent
    } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    if (recommendationsPerformed === 'true') return;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(
              new Error(`Server returned ${result.status}`)
            );
          }
          return;
        }

        if (!result.data?.trim().length) {
          this.#handleError(
            new Error('No recommendations available')
          );
          return;
        }

        // Odczytaj HTML zwrocony przez Shopify
        const parsed = new DOMParser().parseFromString(
          result.data,
          'text/html'
        );

        // Znajdz odpowiednia sekcje rekomendacji
        const incoming = parsed.getElementById(
          `product-recommendations-${sectionId}`
        );

        if (!incoming) {
          throw new Error(
            `Recommendations section ${sectionId} missing in response`
          );
        }

        // Znajdz obecne listy produktow
        const existingLists = Array.from(
          this.querySelectorAll(
            '.section-resource-list > .resource-list'
          )
        );

        // Znajdz nowe listy produktow z Shopify
        const incomingLists = Array.from(
          incoming.querySelectorAll(
            '.section-resource-list > .resource-list'
          )
        );

        if (
          !existingLists.length ||
          existingLists.length !== incomingLists.length
        ) {
          throw new Error(
            'Recommendations list structure does not match'
          );
        }

        // Zachowaj style wygenerowanych kart produktow
        const sectionWrapper = this.closest('.shopify-section');

        const newStylesheet = parsed.querySelector(
          'style[data-section-stylesheet]'
        );

        if (sectionWrapper && newStylesheet) {
          const currentStylesheet = sectionWrapper.querySelector(
            'style[data-section-stylesheet]'
          );

          if (currentStylesheet) {
            currentStylesheet.textContent =
              newStylesheet.textContent;
          } else {
            sectionWrapper.prepend(
              newStylesheet.cloneNode(true)
            );
          }
        }

        // Podmien listy produktow bez przebudowy calej sekcji
        existingLists.forEach((list, index) => {
          list.replaceWith(
            incomingLists[index].cloneNode(true)
          );
        });

        // Oznacz jako zaladowane dopiero po podmianie
        this.dataset.recommendationsPerformed = 'true';
      })
      .catch((error) => this.#handleError(error));
  }

  /**
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<
   *   {success: true, data: string} |
   *   {success: false, status: number}
   * >}
   */
  async #fetchCachedRecommendations(
    productId,
    sectionId,
    intent
  ) {
    const url =
      `${this.dataset.url}` +
      `&product_id=${productId}` +
      `&section_id=${sectionId}` +
      `&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];

    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.#activeFetch.signal
      });

      if (!response.ok) {
        return {
          success: false,
          status: response.status
        };
      }

      const result = await response.text();

      this.#cachedRecommendations[url] = result;

      return {
        success: true,
        data: result
      };
    } finally {
      this.#activeFetch = null;
    }
  }

  /** @param {Error} error */
  #handleError(error) {
    console.error(
      'Product recommendations error:',
      error.message
    );

    this.classList.add('hidden');

    this.dataset.error =
      'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    ProductRecommendations
  );
}
